import { Alert, AlertTitle, Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { formatMoney, CURRENCIES, DEFAULT_CURRENCY } from '../../../../utils/currency';
import {
  MAX_SPLIT_PARTS, SPLIT_METHODS, collectedAmount, isPosPart, newPart, partAmount, remainingAmount, round2, splitError,
} from './splitPayments';

// Medios que se pueden combinar: Mercado Pago QR y PedidosYa tienen su propio flujo y no se dividen.
const methodOptions = (posAvailable) => [
  { value: SPLIT_METHODS.CASH, label: 'Efectivo' },
  posAvailable
    ? { value: SPLIT_METHODS.CARD, label: 'Tarjeta (POS)' }
    : { value: SPLIT_METHODS.CARD, label: 'Crédito' },
  ...(posAvailable ? [] : [{ value: SPLIT_METHODS.DEBIT, label: 'Débito' }]),
  { value: SPLIT_METHODS.TRANSFER, label: 'Transferencia' },
];

const SplitPaymentPanel = ({
  parts,
  onPartsChange,
  due,
  currency = DEFAULT_CURRENCY,
  ticketRate,
  posAvailable = false,
  unbilledPayments = [],
  onUseUnbilled,
}) => {
  const foreignTicket = currency !== DEFAULT_CURRENCY && Boolean(ticketRate);
  const remaining = remainingAmount(due, parts);
  const error = splitError(parts, due);
  const locked = (part) => Boolean(part.transactionId);
  const usedTransactions = new Set(parts.map((part) => part.transactionId).filter(Boolean));

  const update = (uid, patch) => onPartsChange(parts.map((part) => (part.uid === uid ? { ...part, ...patch } : part)));
  const remove = (uid) => onPartsChange(parts.filter((part) => part.uid !== uid));
  const add = () => onPartsChange([...parts, newPart(SPLIT_METHODS.CASH, Math.max(remaining, 0), currency)]);

  // Un cobro del POS aprobado y sin facturar puede ser una de las partes si cabe en lo que falta cobrar.
  const assignableUnbilled = unbilledPayments.filter((payment) => {
    if (usedTransactions.has(payment.transactionId)) return false;
    const chargedCurrency = Object.values(CURRENCIES).find((c) => c.isoNumeric === payment.currency)?.code || DEFAULT_CURRENCY;
    const inTicketCurrency = chargedCurrency === currency ? Number(payment.amount) : (ticketRate ? Number(payment.amount) / ticketRate : null);
    return inTicketCurrency != null && inTicketCurrency > 0 && inTicketCurrency <= remaining + 0.005;
  });

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.75, lineHeight: 1.5 }}>
        {`Dividir ${formatMoney(due, currency)} entre varios medios`}
      </Typography>

      {parts.map((part, index) => {
        const pos = isPosPart(part, posAvailable);
        const collected = collectedAmount(part, currency, ticketRate);
        return (
          <Box
            key={part.uid}
            data-testid={`split-part-${index}`}
            sx={{ display: 'grid', gridTemplateColumns: foreignTicket ? '1.3fr 1fr 1fr auto' : '1.4fr 1fr auto', gap: 1, alignItems: 'start', mb: 1 }}
          >
            <TextField
              select
              size="small"
              label="Medio"
              value={part.method}
              disabled={locked(part)}
              onChange={(event) => update(part.uid, { method: event.target.value })}
              inputProps={{ 'data-testid': `split-method-${index}` }}
            >
              {methodOptions(posAvailable).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={`Monto (${CURRENCIES[currency]?.symbol || currency})`}
              value={part.amount}
              disabled={locked(part)}
              onChange={(event) => update(part.uid, { amount: event.target.value })}
              inputProps={{ inputMode: 'decimal', 'data-testid': `split-amount-${index}` }}
              helperText={locked(part)
                ? 'Cobro aprobado en el POS'
                : (part.currency !== currency && partAmount(part) > 0 ? `Se cobra ${formatMoney(collected, part.currency)}` : (pos ? 'Se cobra en el POS' : ' '))}
              InputProps={locked(part) ? { endAdornment: <LockOutlinedIcon fontSize="small" /> } : undefined}
            />
            {foreignTicket && (
              <TextField
                select
                size="small"
                label="Cobrar en"
                value={part.currency}
                disabled={locked(part)}
                onChange={(event) => update(part.uid, { currency: event.target.value })}
                inputProps={{ 'data-testid': `split-currency-${index}` }}
              >
                <MenuItem value={currency}>{CURRENCIES[currency]?.label || currency}</MenuItem>
                <MenuItem value={DEFAULT_CURRENCY}>{CURRENCIES[DEFAULT_CURRENCY].label}</MenuItem>
              </TextField>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
              <Button
                size="small"
                disabled={locked(part) || remaining === 0 || (remaining < 0 && partAmount(part) + remaining <= 0)}
                onClick={() => update(part.uid, { amount: String(round2(Math.max(partAmount(part) + remaining, 0))) })}
                title="Completar este pago con lo que falta"
              >
                Completar
              </Button>
              <IconButton
                aria-label="Quitar medio de pago"
                size="small"
                disabled={locked(part) || parts.length <= 2}
                onClick={() => remove(part.uid)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
        <Button size="small" onClick={add} disabled={parts.length >= MAX_SPLIT_PARTS}>Agregar medio</Button>
        <Typography
          variant="body2"
          data-testid="split-remaining"
          color={Math.abs(remaining) <= 0.005 ? 'success.main' : 'text.secondary'}
          sx={{ fontWeight: 600 }}
        >
          {Math.abs(remaining) <= 0.005
            ? 'Cubre el total'
            : remaining > 0
              ? `Falta cobrar ${formatMoney(remaining, currency)}`
              : `Se pasa por ${formatMoney(Math.abs(remaining), currency)}`}
        </Typography>
      </Box>

      {assignableUnbilled.length > 0 && onUseUnbilled && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Cobro aprobado sin facturar</AlertTitle>
          {assignableUnbilled.map((payment) => (
            <Box key={payment.transactionId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              <Typography variant="body2">
                {formatMoney(Number(payment.amount), Object.values(CURRENCIES).find((c) => c.isoNumeric === payment.currency)?.code || DEFAULT_CURRENCY)}
                {payment.cardBrand ? ` · ${payment.cardBrand}` : ''}
                {payment.cardLast4 ? ` ****${payment.cardLast4}` : ''}
              </Typography>
              <Button size="small" variant="outlined" color="inherit" onClick={() => onUseUnbilled(payment)}>Usar como un pago</Button>
            </Box>
          ))}
        </Alert>
      )}

      {error && parts.every((part) => partAmount(part) > 0) && Math.abs(remaining) > 0.005 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Los pagos deben sumar exactamente el total para poder cobrar.
        </Typography>
      )}
    </Box>
  );
};

export default SplitPaymentPanel;
